import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { startCase } from 'lodash';
import styled from 'styled-components';

import Tooltip from 'components/shared/Tooltip';
import { simpleDate } from 'lib/date';
import Workflow from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { coveragesOf } from './eligibility/coverageTabs';
import { VALUE_NAMES } from './constants';
import { COVERAGE_SEQUENCES } from './coverages';
import RevealableSsn from './RevealableSsn';

// The at-a-glance facts under the referral title (UX, 2026-08-04): who the patient is, who insures
// them, and where the referral came from, so a coordinator doesn't have to open the extraction card
// to check a DOB or walk the workflow steps to see the source (OLIO-3378).
//
// Every chip is conditional. A referral whose packet never stated a DOB shows no DOB chip rather
// than an empty one, and a referral with no coverages shows no payer chips — the row is a summary
// of what's known, not a form with blanks.
//
// The payer chips read the referral's LIVE coverages, not the `coverages` extraction snapshot.
// The snapshot is whatever the AI last read off the packet, so editing an insurance, adding one, or
// archiving one left these chips showing the old payer indefinitely (OLIO-3141). The extraction
// card still reads the snapshot, and should: that card's job is to show what was read.
//
// The SSN is masked here exactly as it is on the extraction card. The full number lives only in
// the entry form.
export default function PatientSummaryChips({ workflow }: { workflow: Workflow }) {
  // `facetId` reads a value belonging to one coverage. A coverage-scoped value is never on the
  // referral itself, so an unscoped read would miss it entirely.
  const value = (name: string, facetId?: string) => {
    const raw = workflow.valueFor(name, facetId ? { facetId } : undefined)?.value;
    return typeof raw === 'string' && raw ? raw : null;
  };

  // The name the referral RESOLVED to, never the wording its packet used — the same read the list's
  // Referral Source column makes, so the two screens can't name one referral two different ways
  // (OLIO-3158). It follows that an unresolved wording shows no chip at all: while the Confirm
  // Referral Source step is still asking where this one belongs, the header has no answer to give,
  // and the wording itself is on the extraction card where it belongs.
  const referralSource = workflow.resolvedAttrValue(VALUE_NAMES.REFERRAL_SOURCE);
  const dob = simpleDate(value(VALUE_NAMES.PATIENT_DOB));
  const gender = value(VALUE_NAMES.PATIENT_GENDER);
  const ssn = value(VALUE_NAMES.PATIENT_SSN);

  const chips: { key: string; label: string; text: ReactNode; tooltip?: string }[] = [
    // First, ahead of the patient's own facts: it answers "who sent this?", which is the question
    // the coordinator opens the referral with.
    ...(referralSource
      ? [{ key: 'referral-source', label: 'Referral Source', text: referralSource, tooltip: referralSource }]
      : []),
    ...(dob ? [{ key: 'dob', label: 'DOB', text: dob }] : []),
    ...(gender ? [{ key: 'gender', label: 'Gender', text: startCase(gender) }] : []),
    // Masked with a reveal control, through the same component the extraction card uses.
    ...(ssn ? [{ key: 'ssn', label: 'SSN', text: <RevealableSsn ssn={ssn} /> }] : []),
    // One chip per live coverage that has a payer to name — a coverage without its payer has
    // nothing to show here, and belongs on the extraction card where the member id sits.
    //
    // Keyed and labelled by the coverage itself, never by its place in the list: rank IS which
    // insurance it is, so a referral holding only a secondary labels it Secondary and leaves the
    // primary unmentioned rather than promoting it (OLIO-3141). A rank with no declared word gets
    // no chip, the same as before — a label is never invented.
    ...coveragesOf(workflow).flatMap((facet) => {
      const label = startCase(facet.label ?? COVERAGE_SEQUENCES[facet.position] ?? '');
      const payerName = value(VALUE_NAMES.PAYER_NAME, facet.id);
      return label && payerName ? [{ key: `coverage-${facet.id}`, label, text: payerName, tooltip: payerName }] : [];
    }),
  ];

  if (chips.length === 0) return null;

  return (
    <Chips data-testid='patient-summary-chips'>
      {chips.map((chip) => (
        <SummaryChip key={chip.key} label={chip.label} text={chip.text} tooltip={chip.tooltip} />
      ))}
    </Chips>
  );
}

// One chip, which measures whether its own text is being clipped by the 240px cap and offers the
// full value on hover only when it is — the same approach the insights stat tiles take
// (careTransitions/shared/slideout/statTiles.tsx). Measuring is what keeps the tooltip meaningful:
// wrapping every chip would pop a tooltip repeating a name already fully on screen.
//
// A chip with no `tooltip` gets no tooltip element at all, which is how the SSN stays out of one.
// Its digits are masked on screen for a reason, and a tooltip is one more place that masking
// wouldn't reach.
function SummaryChip({ label, text, tooltip }: { label: string; text: ReactNode; tooltip?: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !tooltip) {
      setTruncated(false);
      return;
    }
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth);
    measure();
    // The row wraps and the header is about to become responsive, so a chip can cross the
    // threshold without its text ever changing.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tooltip]);

  const chip = (
    <Chip ref={ref}>
      <ChipLabel>{label}:</ChipLabel> {text}
    </Chip>
  );

  if (!tooltip) return chip;

  return (
    <Tooltip text={tooltip} disabled={!truncated}>
      {chip}
    </Tooltip>
  );
}

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`;

// Capped and clipped, the same treatment the patient story header gives its own title and DOB
// (PatientHeader's StyledPatientHeader / Dob). Referral sources and payers are free text off a
// packet — "Saint Mary's Regional Medical Center of Northern Indiana" is an ordinary name, and
// with `nowrap` alone one chip grew past the header and pushed the row sideways. The cap is what
// keeps the row readable at narrow widths, which is where the responsive pass has to start.
//
// A clipped chip reveals its full text on hover instead, through the shared Tooltip — see
// SummaryChip, which measures whether this cap actually bit before offering one.
const Chip = styled(BodySmall)`
  background: ${colors.black5};
  border-radius: 4px;
  color: ${colors.black100};
  padding: 4px 10px;
  white-space: nowrap;
  font-size: 12px;
  max-width: 240px;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const ChipLabel = styled.span`
  color: ${colors.black100};
`;
